import useCurrencyStore, { DEFAULT_USD } from '../../Store/currencyStore';

/**
 * Convert a USD amount using the given currency's conversion rate.
 */
export const convertFromUsd = (usdAmount, currency = DEFAULT_USD) => {
    const rate = Number(currency?.conversionRate) || 1;
    const amount = Number(usdAmount) || 0;
    return amount * rate;
};

/**
 * Format a USD amount for display.
 * @param {number|string} usdAmount - Amount in USD from the backend
 * @param {object} [options]
 * @param {boolean} [options.forceUsd] - Always show USD (billing page)
 * @param {object} [options.currency] - Override currency (defaults to store selection)
 * @param {'code'|'symbol'} [options.style] - 'code' → "PKR 100.00", 'symbol' → "₨ 100.00"
 * @param {number} [options.decimals] - Decimal places (default 2)
 */
export const formatMoney = (usdAmount, options = {}) => {
    const {
        forceUsd = false,
        currency: currencyOverride,
        style = 'code',
        decimals = 2,
    } = options;

    const storeCurrency = useCurrencyStore.getState().selectedCurrency;
    const currency = forceUsd
        ? DEFAULT_USD
        : (currencyOverride || storeCurrency || DEFAULT_USD);

    const converted = convertFromUsd(usdAmount, currency);
    const formatted = converted.toFixed(decimals);

    if (style === 'symbol') {
        return `${currency.symbol || '$'}\u00A0${formatted}`;
    }

    return `${currency.currencyCode || 'USD'}\u00A0${formatted}`;
};

/**
 * Hook so components re-render when the selected currency changes.
 */
export const useFormatMoney = () => {
    const selectedCurrency = useCurrencyStore((state) => state.selectedCurrency);

    return (usdAmount, options = {}) =>
        formatMoney(usdAmount, {
            ...options,
            currency: options.forceUsd
                ? DEFAULT_USD
                : (options.currency || selectedCurrency),
        });
};

export const getDisplayCurrency = () => {
    return useCurrencyStore.getState().selectedCurrency || DEFAULT_USD;
};

export default formatMoney;
